/* eslint-disable no-nested-ternary */
import dayjs from "dayjs";
import { axiosInstance } from "../axios";
import {
  AppKey,
  AppKeyCreate,
  AppKeyImport,
  CertifcateInfo,
  Issuer,
  IssuerCreate,
  KeyCategory,
  RequestResponse,
  ResourceFetchParams,
  Scheme
} from "../types";
import { MockIssuerService } from "./mockService";
import { isKeyZMK } from "../validation-helpers";

export const getIssuers: (
  params: ResourceFetchParams
) => Promise<RequestResponse<{ issuers: Issuer[]; count: number }>> = async ({
  pageNumber,
  pageSize,
  searchStr,
  code
}) => {
  try {
    let endpoint;
    if (code) {
      endpoint = `/issuers/get-by-code/${code}`;
    } else if (searchStr) {
      endpoint = `/issuers/get-by-name?issuerName=${searchStr}`;
    } else {
      endpoint = `/issuers/get-all?page=${pageNumber}&size=${pageSize}`;
    }

    const response = await axiosInstance.get(endpoint);
    const issuers = code
      ? response.data.data
        ? [response.data.data]
        : []
      : searchStr
      ? (response.data.data || []).slice(
          (pageNumber - 1) * pageSize,
          pageNumber * pageSize
        )
      : response.data.data?.content || [];
    const count = code
      ? issuers.length
      : searchStr
      ? response.data.data.length
      : response.data.data?.count || 0;

    return {
      error: false,
      data: {
        issuers: issuers.map(
          (issuer: any) =>
            ({
              ...issuer,
              keys: []
            } as Issuer)
        ),
        count
      }
    };
  } catch (err) {
    console.error("Unable to get issuers: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const createIssuer: (
  issuer: IssuerCreate
) => Promise<RequestResponse> = async issuer => {
  try {
    await axiosInstance.post("/issuers", issuer);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to create issuer: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const updateIssuer: (
  issuer: Issuer
) => Promise<RequestResponse> = async issuer => {
  try {
    await axiosInstance.put(`/issuers/update/${issuer.id}`, issuer);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to update issuer: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const getIssuerKeys: (
  issuerCode: string,
  pageNumber: number,
  pageSize: number,
  keyCategory: string
) => Promise<RequestResponse<{ keys: AppKey[]; total: number }>> = async (
  issuerCode,
  pageNumber,
  pageSize,
  keyCategory
) => {
  try {
    let response;
    if (keyCategory === "RSA") {
      response = await axiosInstance.get(
        `/rsa-key/get-issuer-keys?issuerCode=${issuerCode}&page=${pageNumber}&size=${pageSize}`
      );
    } else {
      response = await axiosInstance.get(
        `/des-key/get-issuer-keys?issuerCode=${issuerCode}&page=${pageNumber}&size=${pageSize}`
      );
    }

    const keys = response.data.content.map((key: any) => ({
      ...key,
      keyCategory: keyCategory === "RSA" ? "RSA" : "DES",
      desKeyInfoId: keyCategory === "RSA" ? -1 : undefined,
      envFlag: key.envFlag || "-",
      keyExponentValue:
        keyCategory === "RSA" ? String(key.keyExponentValue) : undefined,
      keyLength: keyCategory === "RSA" ? key.keyModulusLength : undefined,
      hasKey:
        keyCategory === "RSA"
          ? key.hasKey
          : Boolean(key.keyValueUnderZmk || key.keyValueUnderLmk),
      keyValue: keyCategory !== "RSA" ? key.keyValueUnderZmk : undefined,
      lastUpdatedOn: key.lastUpdatedOn
        ? dayjs(key.lastUpdatedOn).format("D MMMM YYYY")
        : "-",
      activationDate: key.activationDate
        ? dayjs(key.activationDate).format("MMYY")
        : "",
      expiryDate: key.expiryDate ? dayjs(key.expiryDate).format("MMYY") : ""
    }));

    const total = response.data.count;

    return {
      error: false,
      data: {
        keys,
        total
      }
    };
  } catch (err) {
    console.error("Unable to get issuer keys: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

const getKeyPayload = (
  key: AppKeyCreate | AppKey,
  issuerCode: string,
  selectedKeyInfo: KeyCategory
) => {
  const payload: any = {
    issuerCode,
    expiryDate: `01/${key.expiryDate.slice(0, 2)}/20${key.expiryDate.slice(2)}`,
    envFlag: key.envFlag,
    description: key.keyDescription,
    keyIndex: key.keyIndex,
    keyVersion: key.keyVersion,
    keyLength:
      selectedKeyInfo.keyCategory !== "RSA" ? key.keyLength : undefined,
    keyModulusLength:
      selectedKeyInfo.keyCategory === "RSA" ? key.keyLength : undefined,
    keyExponentValue:
      selectedKeyInfo.keyCategory === "RSA" ? key.keyExponentValue : undefined,
    issuerBin:
      selectedKeyInfo.keyCategory === "RSA" ? key.issuerBin : undefined,
    desKeyInfoId: selectedKeyInfo.id
  };
  return payload;
};

export const createIssuerKey: (
  appKey: AppKeyCreate,
  issuerCode: string,
  selectedKeyInfo: KeyCategory
) => Promise<RequestResponse> = async (appKey, issuerCode, selectedKeyInfo) => {
  try {
    await axiosInstance.post(
      `/${
        selectedKeyInfo.keyCategory === "RSA" ? "rsa-key" : "des-key"
      }/create`,
      getKeyPayload(appKey, issuerCode, selectedKeyInfo)
    );
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to create issuer key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const generateRSAValue: (
  rsaKey: AppKey,
  issuerCode: string,
  certData: {
    schemeId: Scheme;
    modeFlag: string;
    certificateSerialNumber?: string;
    issuerPublicKeyIndex?: string;
    serviceIdentifier?: string;
    trackingNumber?: string;
  }
) => Promise<RequestResponse<any>> = async (appKey, issuerCode, certData) => {
  try {
    const certificate = await axiosInstance.post("/generate-issuer-cert", {
      schemeId: certData.schemeId,
      modeFlag: certData.modeFlag,
      certificateSerialNumber:
        certData.schemeId === "JCB" || certData.schemeId === "MASTERCARD"
          ? certData.certificateSerialNumber
          : undefined,
      issuerPublicKeyIndex:
        certData.schemeId === "JCB" || certData.schemeId === "MASTERCARD"
          ? certData.issuerPublicKeyIndex
          : undefined,
      serviceIdentifier:
        certData.schemeId === "VISA" ||
        certData.schemeId === "AMEX" ||
        certData.schemeId === "UNION_PAY"
          ? certData.serviceIdentifier
          : undefined,
      trackingNumber:
        certData.schemeId === "VISA" ||
        certData.schemeId === "AMEX" ||
        certData.schemeId === "UNION_PAY"
          ? certData.trackingNumber
          : undefined,
      rsaKeyLabel: appKey.keyLabel,
      issuerCode
    });
    return {
      data: certificate,
      error: false
    };
  } catch (err) {
    console.error("Unable to generate RSA value: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const downloadKeyRequest: (payload: {
  key: AppKey;
  scheme: Scheme;
}) => Promise<RequestResponse> = async ({ key, scheme }) => {
  try {
    const response = await axiosInstance.get(
      `/certificate/generate-key-request?cardScheme=${scheme}&keyLabel=${key.keyLabel}`,
      { responseType: "blob" }
    );
    const fileName = response.headers["content-disposition"]
      .split("; ")
      .find((section: string) => section.includes("filename"))
      ?.replace(/(filename|=|")/g, "");
    return {
      error: false,
      data: {
        file: response.data,
        fileName
      }
    };
  } catch (err) {
    const message =
      JSON.parse(await err.response?.data?.text()).responseMessage ||
      err.message;
    console.error("Unable to download key request: ", message);
    return {
      error: true,
      data: null,
      message
    };
  }
};

export const getCertificateInfo: (
  rsaKeyLabel: string
) => Promise<RequestResponse<CertifcateInfo>> = async rsaKeyLabel => {
  try {
    const response = await axiosInstance.get(
      `/certificate/get-cert-by-label?keyLabel=${rsaKeyLabel}`
    );
    return {
      error: false,
      data: {
        ...response.data.data,
        createdOn: dayjs(response.data.data.createdOn),
        lastUpdatedOn: dayjs(response.data.data.lastUpdatedOn)
      }
    };
  } catch (err) {
    console.error("Unable to get certificate data: ", err);
    return {
      error: true,
      data: null,
      message: err.response?.data?.responseMessage || err.message
    };
  }
};

export const uploadSignedCertificate: (payload: {
  key: AppKey;
  caKeyId: number;
  scheme: Scheme;
  file: File;
}) => Promise<RequestResponse> = async ({ key, caKeyId, scheme, file }) => {
  try {
    const formData = new FormData();
    formData.append("issuer-cert-file", file);
    await axiosInstance.post(
      `/certificate/import-issuer-cert?scheme=${scheme}&issuerKeyLabel=${key.keyLabel}&caKeyId=${caKeyId}`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data"
        }
      }
    );
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to upload signed certificate: ", err);
    return {
      error: true,
      data: null,
      message: err.response?.data?.responseMessage || err.message
    };
  }
};

export const updateIssuerKey: (
  issuerKey: AppKey,
  issuerCode: string,
  selectedKeyInfo: KeyCategory
) => Promise<RequestResponse> = async (appKey, issuerCode, selectedKeyInfo) => {
  try {
    await axiosInstance.patch(
      `/${appKey.keyCategory === "RSA" ? "rsa-key" : "des-key"}/update/${
        appKey.id
      }`,
      getKeyPayload(appKey, issuerCode, selectedKeyInfo)
    );
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to update issuer key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const deleteIssuerKey: (
  key: AppKey,
  issuerCode: string
) => Promise<RequestResponse> = async (appKey, issuerCode) => {
  try {
    await MockIssuerService.deleteIssuerKey(appKey, issuerCode);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error("Unable to delete issuer key: ", err.response?.data || err);
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const importIssuerKeyValue: (
  payload: AppKeyImport,
  key: AppKey
) => Promise<RequestResponse> = async (payload, appKey) => {
  try {
    const trueValue = payload.keyValue.replace(/\s/g, "");
    await axiosInstance.post(
      `/des-key/import-${isKeyZMK(appKey) ? "zmk" : "key"}`,
      {
        keyLabel: appKey.keyLabel,
        keyValue: trueValue,
        kcv: payload.kcv.replace(/\s/g, ""),
        parentLabel: payload.parentLabel || undefined,
        encryptionMode: payload.encryptionMode || undefined,
        keyScheme: payload.keyScheme || undefined
      }
    );
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error(
      "Unable to import issuer key value: ",
      err.response?.data || err
    );
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const toggleKeyActivation: (
  key: AppKey,
  newStatus: boolean
) => Promise<RequestResponse> = async (appKey, activated) => {
  try {
    const endpoint =
      appKey.keyCategory === "RSA"
        ? `/rsa-key/${activated ? "activate-key" : "deactivate-key"}/${
            appKey.id
          }`
        : `des-key/${activated ? "activate-key" : "deactivate-key"}/${
            appKey.id
          }`;
    await axiosInstance.get(endpoint);
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error(
      "Unable to toggle issuer activation: ",
      err.response?.data || err
    );
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};

export const autoGenerateKeyValue: (
  key: AppKey
) => Promise<RequestResponse> = async appKey => {
  try {
    await axiosInstance.post("/des-key/generate-key", {
      keyLabel: appKey.keyLabel
    });
    return {
      error: false,
      data: null
    };
  } catch (err) {
    console.error(
      "Unable to auto generate key value: ",
      err.response?.data || err
    );
    return {
      error: true,
      message: err.response?.data?.responseMessage || err.message,
      data: null
    };
  }
};
